import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { App as AntdApp, ConfigProvider } from 'antd'
import { useTranslation } from 'react-i18next'
import { ANTD_LOCALES } from '@/app/i18n/antdLocales'
import { currentLanguage, isRtl } from '@/app/i18n'
import { ThemeContext, type ThemeContextValue, type ThemeMode } from './ThemeContext'
import { HEADER_BG, darkTheme, lightTheme } from './tokens'

const STORAGE_KEY = 'xsib.theme'

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // localStorage mavjud bo'lmasligi mumkin
  }
  // Saqlangan tanlov yo'q — standart rejim QORONG'I (07-FRONTEND.md §4).
  // Tizim sozlamasiga qaralmaydi: operatorlar panelni kun bo'yi ochiq
  // tutadi va MVP'dan beri odatlangan ko'rinish shu.
  return 'dark'
}

/**
 * antd `ConfigProvider` — IKKI narsani bir vaqtda beradi:
 *   1. tema (algoritm + tokenlar)
 *   2. antd komponentlarining O'Z tili (`locale`)
 *
 * Ikkinchisini unutish oson va oqibati ko'zga tashlanadi: o'zbekcha panelda
 * bo'sh jadval tagida inglizcha "No data" turadi (07-FRONTEND.md §5).
 * Shuning uchun ikkalasi shu bitta joyda, birga turadi.
 */
export function AppThemeProvider({ children }: { children: ReactNode }) {
  // `useTranslation` faqat tarjima uchun emas — til o'zgarganda shu komponent
  // qayta chizilishi uchun ham kerak, aks holda antd locale eskisida qolardi
  const { i18n } = useTranslation()
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // e'tiborsiz
    }
    // Brauzerga ham aytamiz: scrollbar va forma elementlari o'z ranglarini
    // shundan oladi — aks holda qorong'i sahifada oq scrollbar qoladi
    document.documentElement.style.colorScheme = mode
    document.documentElement.dataset['theme'] = mode
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', HEADER_BG[mode])
  }, [mode])

  const setMode = useCallback((next: ThemeMode) => setModeState(next), [])
  const toggleMode = useCallback(
    () => setModeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    [],
  )

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, isDark: mode === 'dark', setMode, toggleMode }),
    [mode, setMode, toggleMode],
  )

  const antdLocale = ANTD_LOCALES[currentLanguage()]
  void i18n.resolvedLanguage // til o'zgarishiga bog'liqlikni aniq ko'rsatadi

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={mode === 'dark' ? darkTheme : lightTheme} locale={antdLocale} direction={isRtl() ? 'rtl' : 'ltr'}>
        {/* antd `App` — `message`/`notification`/`modal` ni hook orqali
            ishlatish uchun. Statik `message.success(...)` tema tokenlarini
            OLMAYDI (u React daraxtidan tashqarida chaqiriladi) va qorong'i
            rejimda oq quti bo'lib chiqadi. */}
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  )
}
